// src/utils/gridSetup.ts
//
// Shared building blocks for turning an imported subject list into a grid's
// classes and its line-subject availability. Used by both the guided Grid Setup
// wizard (src/pages/grid/GridSetupWizard.tsx) and the Grid Import modal, so the
// two paths produce identical data and write to the same place the Timetable
// Editor reads.
//
// The Timetable Editor reads subject-on-line availability from the
// `line_subject_config` table (one row per grid, keyed by config_name). See
// LineSubjectConfig.tsx and EnhancedMultiGridEditor.tsx. Anything that captures
// "which subject runs on which line" MUST write here — not the `settings` table,
// which nothing reads for this purpose.

import { supabase } from '../supabase/config';
import { StaffAllocationGrid, ClassAllocation, LineDefinition } from '../types/grid';
import { LineSubjectMapping, SubjectLineAvailability } from '../types/lineSubject';
import { SubjectData } from './csvParser';

// The single config row the Timetable Editor reads back per grid. Kept in step
// with CONFIG_NAME in LineSubjectConfig.tsx (unique key is (grid_id, config_name)).
export const LINE_SUBJECT_CONFIG_NAME = 'main_grid_config';

/**
 * Resolve a CSV line value ("A") to an actual grid line id, mirroring the fuzzy
 * lookup the editor uses (isSubjectAvailableOnLine): match by id, name, or first
 * letter. Falls back to the raw value so nothing is silently dropped.
 */
export function resolveLineId(rawLine: string, lines: LineDefinition[]): string {
  const value = rawLine.trim();
  if (!value) return value;
  const match =
    lines.find((l) => l.id?.toLowerCase() === value.toLowerCase()) ||
    lines.find((l) => l.name?.toLowerCase() === value.toLowerCase()) ||
    lines.find((l) => l.id?.charAt(0).toLowerCase() === value.charAt(0).toLowerCase());
  return match ? match.id : value;
}

/**
 * Aggregate, per subject code, the set of grid line ids that code is allowed to
 * run on (from every CSV row carrying a Line for that code).
 */
function allowedLinesBySubjectCode(
  subjects: SubjectData[],
  lines: LineDefinition[]
): Map<string, string[]> {
  const byCode = new Map<string, Set<string>>();
  for (const subject of subjects) {
    const code = subject.code?.trim();
    const rawLine = subject.line?.trim();
    if (!code || !rawLine) continue;
    const lineId = resolveLineId(rawLine, lines);
    if (!byCode.has(code)) byCode.set(code, new Set());
    byCode.get(code)!.add(lineId);
  }
  const out = new Map<string, string[]>();
  for (const [code, set] of byCode) out.set(code, Array.from(set).sort());
  return out;
}

/**
 * Turn parsed subjects into ClassAllocation rows for a grid. Classes start with
 * no line assignments — teachers are placed later in the Timetable Editor. When
 * the CSV carries a Line column, each class records which lines it may run on in
 * `allowedLineIds` (the source of truth the editor prefers, alongside the
 * line_subject_config fallback for older grids).
 */
export function buildClassesFromSubjects(
  subjects: SubjectData[],
  grid: Pick<StaffAllocationGrid, 'id' | 'defaultClassMinutes' | 'lines'>,
  now: number
): ClassAllocation[] {
  const allowedByCode = allowedLinesBySubjectCode(subjects, grid.lines || []);
  return subjects
    .filter((s) => s.name?.trim() && s.code?.trim())
    .map((subject, index) => {
      const code = subject.code.trim();
      const allowed = allowedByCode.get(code);
      return {
        id: `${grid.id}_${code}_${now}_${index}`,
        className: subject.name.trim(),
        subjectCode: code,
        curriculumArea: subject.faculty?.trim() || 'General',
        lineAssignments: {},
        ...(allowed && allowed.length > 0 ? { allowedLineIds: allowed } : {}),
        studentCount: 0,
        minutesPerWeek: grid.defaultClassMinutes || 70,
      };
    }) as ClassAllocation[];
}

/**
 * Build a subject→line availability map from classes that carry `allowedLineIds`.
 * Keys each class under its subjectCode, className, and a normalised className,
 * matching the lookup keys createSubjectAvailabilityMap / isSubjectAvailableOnLine
 * use, so the editor can gate cells the same way regardless of the source.
 * Returns an empty map when no class carries availability (legacy grids), so the
 * caller can fall back to the line_subject_config table.
 */
export function availabilityFromClasses(classes: ClassAllocation[]): SubjectLineAvailability {
  const map: SubjectLineAvailability = {};
  const addKey = (key: string | undefined, lineId: string) => {
    if (!key) return;
    if (!map[key]) map[key] = {};
    map[key][lineId] = true;
  };
  for (const cls of classes || []) {
    const allowed = cls.allowedLineIds;
    if (!allowed || allowed.length === 0) continue;
    for (const lineId of allowed) {
      addKey(cls.subjectCode, lineId);
      addKey(cls.className, lineId);
      const normalized = cls.className?.replace(/[\s\-_]/g, '').toLowerCase();
      addKey(normalized, lineId);
    }
  }
  return map;
}

/** True when at least one class carries per-class line availability. */
export function hasClassLineAvailability(classes: ClassAllocation[]): boolean {
  return (classes || []).some((c) => c.allowedLineIds && c.allowedLineIds.length > 0);
}

/**
 * Apply a full set of line-subject mappings onto a grid's classes, returning new
 * class objects with `allowedLineIds` set to match. Mappings are authoritative:
 * a class with no matching mapping has any prior availability hint removed, so the
 * Subject Availability page fully controls the result. Matches a class to mappings
 * by subjectCode first, then className.
 */
export function applyAvailabilityToClasses(
  classes: ClassAllocation[],
  mappings: LineSubjectMapping[]
): ClassAllocation[] {
  const byCode = new Map<string, Set<string>>();
  const byName = new Map<string, Set<string>>();
  const add = (map: Map<string, Set<string>>, key: string | undefined, lineId: string) => {
    const k = key?.trim();
    if (!k) return;
    if (!map.has(k)) map.set(k, new Set());
    map.get(k)!.add(lineId);
  };
  for (const m of mappings) {
    add(byCode, m.classCode, m.lineId);
    add(byName, m.className, m.lineId);
  }
  return (classes || []).map((c) => {
    const set = byCode.get(c.subjectCode?.trim() || '') || byName.get(c.className?.trim() || '');
    if (set && set.size > 0) {
      return { ...c, allowedLineIds: Array.from(set).sort() };
    }
    if (c.allowedLineIds) {
      const { allowedLineIds, ...rest } = c;
      return rest as ClassAllocation;
    }
    return c;
  });
}

/**
 * Match each subject that carries a line value against the grid's real lines and
 * produce line-subject availability mappings. A CSV line value ("A") is matched
 * against a grid line by id, name, or first-letter, mirroring the fuzzy lookup
 * the editor uses (isSubjectAvailableOnLine).
 */
export function buildLineSubjectMappings(
  subjects: SubjectData[],
  grid: StaffAllocationGrid
): LineSubjectMapping[] {
  const lines = grid.lines || [];
  const mappings: LineSubjectMapping[] = [];

  for (const subject of subjects) {
    const rawLine = subject.line?.trim();
    if (!rawLine || !subject.code?.trim()) continue;

    // Resolve the CSV line value to an actual grid line id.
    const lineId = resolveLineId(rawLine, lines);
    const line = lines.find((l) => l.id === lineId) || null;

    mappings.push({
      id: `${lineId}-${subject.code.trim()}`,
      lineId,
      lineName: line?.name || `Line ${lineId}`,
      classCode: subject.code.trim(),
      className: subject.name?.trim() || subject.code.trim(),
      faculty: subject.faculty?.trim() || 'General',
      gridId: grid.id,
      gridName: grid.name,
    });
  }

  return mappings;
}

/**
 * Persist line-subject availability to the `line_subject_config` table — the
 * table the Timetable Editor reads. Upserts one row per grid on
 * (grid_id, config_name). Throws on error so callers can surface it.
 */
export async function saveLineAvailability(
  gridId: string,
  mappings: LineSubjectMapping[]
): Promise<void> {
  const { error } = await supabase.from('line_subject_config').upsert(
    {
      grid_id: gridId,
      config_name: LINE_SUBJECT_CONFIG_NAME,
      mappings,
      updated_at: new Date().toISOString(),
    },
    // The unique index is tenant-scoped: (tenant_id, grid_id, config_name).
    // Naming only (grid_id, config_name) makes Postgres reject the upsert with
    // 42P10 — no unique or exclusion constraint matches.
    { onConflict: 'tenant_id,grid_id,config_name' }
  );

  if (error) throw error;
}
